import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Tooltip,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import {
  Check,
  Close as CloseIcon,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@material-ui/icons";
import { Empty } from "antd";
import axios from "axios";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useAsync, useAsyncFn, useMedia } from "react-use";
import DeviceImages from "../components/DeviceImages";
import { ReactComponent as DetailsIcon } from "../images/details.svg";
import { ReactComponent as ThumbIcon } from "../images/thumb.svg";

const useStyles = makeStyles(() => ({
  stepLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: 12,
    gap: 25,

    "& .trade-in-status": {
      "@media(max-width: 768px)": {
        display: "flex",
        flexDirection: "column",
      },
      "& .status": {
        fontSize: 14,
      },
      "& .date": {
        fontSize: 14,
        fontWeight: 600,
        color: "var(--gray-9)",
      },
    },
  },
  close: {
    overflow: "hidden",
    maxHeight: 0,
    padding: "0px !important",
    borderBottomRightRadius: "4px !important",
    borderBottomLeftRadius: "4px !important",
  },
  open: {
    maxHeight: 1000,
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& .modal-title": {
      margin: 0,
    },
  },
  shadow: {
    boxShadow: "0px 2px 4px 0px rgba(33, 37, 41, 0.08)",
  },
  mainPadding: {
    paddingInline: 80,
    paddingBlock: 40,
    "@media (max-width: 768px)": {
      padding: "20px 0px",
    },
  },
  detailsHeader: {
    borderRadius: "4px 4px 0px 0px",
    border: "1px solid var(--gray-3)",
    borderBottom: "1px solid var(--gray-2)",
    background: "var(--gray-1)",
    padding: 16,
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    "&.transactionDetails": {
      "@media(max-width: 768px)": {
        flexDirection: "column",
        gap: 18,
        height: "auto",
        alignItems: "start",
      },
    },

    "& .content": {
      display: "flex",
      alignItems: "center",

      "& .texts": {
        marginLeft: 12,
        "@media(max-width: 768px)": {
          display: "flex",
          gap: 8,
          alignItems: "center",
        },
        "& .t1": {
          margin: 0,
          fontSize: 13,
          color: "var(--gray-7)",
          lineHeight: "20px",
        },
        "& .t2": {
          margin: 0,
          fontSize: 16,
          fontWeight: 600,
          color: "var(--gray-9)",
          lineHeight: "24px",
        },
        "& .semibold": {
          fontWeight: 600,
        },

        "& .darker": {
          color: "var(--gray-9)",
        },
      },
    },
  },
  detailsContent: {
    borderBottomRightRadius: 4,
    borderBottomLeftRadius: 4,
    border: "1px solid var(--gray-3)",
    borderTop: "none",
    padding: 20,

    "& .row": {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(256px, 1fr))",
      gridRowGap: 20,

      "& .col": {
        "& .title": {
          margin: 0,
          fontSize: 13,
          color: "var(--gray-7)",
          lineHeight: "20px",
        },
        "& .value": {
          margin: 0,
          fontSize: 16,
          fontWeight: 600,
          color: "var(--gray-9)",
          lineHeight: "24px",
          "&.status": {
            color: "var(--yellow-6)",
          },
        },
      },
    },
  },
  divider: {
    height: 1,
    background: "var(--gray-2)",
    marginBlock: 24,
  },
  sectionTitle: {
    margin: "0px 0px 16px",
    fontSize: 16,
    fontWeight: 600,
    color: "var(--gray-9)",
    lineHeight: "24px",

    "&.marginTop": {
      marginTop: 24,
    },
  },
  viewImagesBtn: {
    fontSize: 13,
    fontWeight: 600,
    color: "#5046B2",
    lineHeight: "18px",
    background: "none",
    border: "none",
    cursor: "pointer",
    marginTop: 20,
    fontFamily: "Inter",

    "& span": {
      marginLeft: 6,
    },
  },
  successText: {
    color: "var(--green-2)",
    fontSize: 14,
    fontWeight: 500,
  },
  customerInfoHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editInfo: {
    fontWeight: 600,
    fontFamily: "Inter",
    textTransform: "capitalize",
  },
  loading: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    flexDirection: "column",

    "& p": {
      marginTop: 16,
    },
  },
  stepperLineVertical: {
    marginTop: 8,
  },
  resendKit: {
    "@media(max-width: 768px)": {
      width: "100%",
    },

    "& .resendkitButton": {
      width: "100%",
    },
  },
}));

const events = [
  "kitCreatedTimestamp",
  "outboundKitSent",
  "boxReceivedTimestamp",
  "deviceReceivedTimestamp",
  "deviceConvertedTimestamp",
  "auditCompleteTimestamp",
  "valueResponseTimestamp",
];

const eventsLabels = {
  kitCreatedTimestamp: "Kit Created",
  outboundKitSent: "Kit Sent",
  boxReceivedTimestamp: "Box Received",
  deviceReceivedTimestamp: "Device Received",
  deviceConvertedTimestamp: "Device Converted",
  auditCompleteTimestamp: "Audit Complete",
  valueResponseTimestamp: "Value Response",
};

const useStepperStyle = makeStyles({
  root: {
    paddingBlock: 0,
    background: "none",
  },
  "@media (max-width: 768px)": {
    root: {
      padding: 0,
    },
  },
});

const useStepperConnectorStyles = makeStyles({
  lineVertical: {
    marginTop: 8,
  },
  line: {
    borderLeftWidth: 2,
  },
  active: {
    "& span": {
      borderColor: "var(--green-2) !important",
    },
  },
  completed: {
    "& span": {
      borderColor: "var(--green-2) !important",
    },
  },
});

const useQontoStepIconStyles = makeStyles({
  root: {
    color: "#eaeaf0",
    display: "flex",
    height: 24,
    width: 24,
    alignItems: "center",
    justifyContent: "center",
    background: "var(--green-2)",
    borderRadius: "50%",
  },
  circle: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    backgroundColor: "white",
    border: "1px solid var(--gray-5)",
  },
  completed: {
    color: "white !important",
    zIndex: 1,
    width: 13,
    height: 25,
  },
});

function QontoStepIcon(props) {
  const classes = useQontoStepIconStyles();
  console.log(props);
  const { completed, active } = props;

  return (
    <div className={clsx(classes.root)}>
      {completed ? (
        <Check className={classes.completed} />
      ) : (
        <div className={clsx(classes.circle, { [classes.active]: active })} />
      )}
    </div>
  );
}

const TradeInProgress = ({ tradeInEvents, tradeInId }) => {
  const classes = useStyles();
  const stepperClasses = useStepperStyle();
  const stepperConnectorClasses = useStepperConnectorStyles();
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {
    if (tradeInEvents) {
      events.forEach((ev, index) => {
        if (tradeInEvents[ev]) setActiveStep(index + 1);
      });
    }
  }, [tradeInEvents]);

  return (
    <Stepper
      classes={stepperClasses}
      connector={<StepConnector classes={stepperConnectorClasses} />}
      activeStep={activeStep}
      orientation="vertical"
    >
      <Step>
        <StepLabel StepIconComponent={QontoStepIcon}>
          <div className={classes.stepLabel}>
            <div className="trade-in-status">
              <span className="status">Creation of transaction - </span>
              <span className="date">
                {tradeInId} | {tradeInEvents?.tradeInDate || "-"}
              </span>
            </div>
            <div className={classes.successText}>Success</div>
          </div>
        </StepLabel>
      </Step>

      {events.map((ev, index) => (
        <Step key={ev}>
          <StepLabel StepIconComponent={QontoStepIcon}>
            <div className={classes.stepLabel}>
              <div className="trade-in-status">
                <span className="status">{eventsLabels[ev]}</span>
                {tradeInEvents[ev] && (
                  <span className="date">{tradeInEvents[ev]}</span>
                )}
              </div>
              {tradeInEvents[ev] ? (
                <div className={classes.successText}>Success</div>
              ) : (
                "Pending"
              )}
            </div>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};

const TradeInTimeLine = ({ tradeInEvents, tradeInId }) => {
  const classes = useStyles();
  const [openTimeLine, toggleOpenTimeLine] = useState(true);
  return (
    <div>
      <h2 className={clsx(classes.sectionTitle, "marginTop")}>
        Trade-In Timeline
      </h2>
      <div className={classes.shadow}>
        <div className={classes.detailsHeader}>
          <div className="content">
            <div className="texts">
              <p className={clsx("t1 semibold darker")}>Transaction Process</p>
            </div>
          </div>
          <div>
            <IconButton onClick={() => toggleOpenTimeLine((prev) => !prev)}>
              {!openTimeLine ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
            </IconButton>
          </div>
        </div>
        <div
          className={clsx(classes.detailsContent, {
            [classes.open]: openTimeLine,
            [classes.close]: !openTimeLine,
          })}
        >
          <TradeInProgress
            tradeInEvents={tradeInEvents}
            tradeInId={tradeInId}
          />
        </div>
      </div>
    </div>
  );
};

const ColorButton = withStyles((theme) => ({
  root: {
    color: "#5046B2",
    borderColor: "#5046B2",
    "&:hover": {
      borderColor: "#413992",
    },
    fontFamily: "Inter",
    fontSize: 14,
  },
}))(Button);

const CloseDialogButton = withStyles((theme) => ({
  root: {
    position: "relative",
    top: -20,
    right: -20,
  },
}))(IconButton);

const TradeInDetails = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const classes = useStyles();
  const [viewDeviceImage, toggleViewDeviceImage] = useState(false);
  const { tradeinid } = useParams();
  const isMobile = useMedia("(max-width: 768px)");

  const getDetails = async () => {
    const quote = await axios.post(
      "/tradeindetails/getDetails",
      {
        filter: tradeinid,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("userId")}`,
        },
      }
    );
    return await quote.data.data;
  };
  const { loading, value } = useAsync(getDetails);

  let tradeInDetails = value?.[0];

  const [resendKitState, postResendKit] = useAsyncFn(async () => {
    const response = await axios.post(
      "/tradeindetails/resendKit",
      { tradeinId: tradeInDetails.id },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("userId")}`,
        },
      }
    );
    return response.data;
  }, [tradeInDetails]);

  const isPendingResendKit = useMemo(() => {
    if (tradeInDetails) {
      return tradeInDetails?.ctiQuoteParent?.resendKit ? true : false;
    }
  }, [tradeInDetails]);

  const isCanceled = useMemo(() => {
    if (tradeInDetails) {
      return (
        tradeInDetails?.ctiQuoteParent?.rkOutsourcerOrderState?.id === 3 ||
        tradeInDetails?.ctiQuoteParent?.rkOutsourcerOrderState?.id === 9
      );
    }
  }, [tradeInDetails]);

  const isDisabledResendKit = useMemo(() => {
    if (tradeInDetails) {
      return tradeInDetails?.ctiQuoteParent?.InboundTrackingNumber
        ?.carrierPickUpTimestamp
        ? true
        : false;
    }
  }, [tradeInDetails]);

  if (loading)
    return (
      <div className={classes.loading}>
        <CircularProgress />
        <p>Loading Details</p>
      </div>
    );

  return !tradeInDetails ? (
    <div
      style={{
        height: 500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Empty />
    </div>
  ) : (
    <>
      <Dialog open={viewDeviceImage}>
        <DialogTitle>
          <div className={classes.modalHeader}>
            <h2 className="modal-title">Device Images</h2>
            <CloseDialogButton
              size="small"
              onClick={() => toggleViewDeviceImage(false)}
            >
              <CloseIcon />
            </CloseDialogButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {tradeInDetails?.pictures.flat().filter((picture) => picture)
            .length ? (
            <div className={classes.viewDeviceImage}>
              <DeviceImages
                images={
                  tradeInDetails?.pictures
                    .flat()
                    .filter((picture) => picture) || []
                }
              />
            </div>
          ) : (
            <p>No images found</p>
          )}
        </DialogContent>
      </Dialog>
      <div className={classes.mainPadding}>
        <div className={classes.shadow}>
          <div className={clsx(classes.detailsHeader, "transactionDetails")}>
            <div className="content">
              <DetailsIcon />
              <div className="texts">
                <p className="t1">{t("tradeInId")}</p>
                <p className="t2">{tradeInDetails?.headerExternalId}</p>
              </div>
            </div>
            <div className={classes.resendKit}>
              <Tooltip
                arrow
                placement="bottom-end"
                title={
                  isCanceled || isDisabledResendKit || isPendingResendKit ? (
                    <>
                      <p color="inherit">{t("Resend Kit is Disabled")}</p>
                      {isCanceled
                        ? t("Trade in has been canceled")
                        : isPendingResendKit
                        ? t("Resend was previously requested")
                        : isDisabledResendKit
                        ? t("Inbound kit has been shipped")
                        : ""}
                    </>
                  ) : (
                    ""
                  )
                }
              >
                <span style={{ display: "flex" }}>
                  <ColorButton
                    className="resendkitButton"
                    onClick={postResendKit}
                    disabled={
                      isCanceled ||
                      isDisabledResendKit ||
                      isPendingResendKit ||
                      resendKitState.loading
                    }
                    variant="outlined"
                  >
                    {resendKitState?.loading
                      ? "Resending Kit..."
                      : t("Resend Kit")}
                  </ColorButton>
                </span>
              </Tooltip>
            </div>
          </div>
          <div className={classes.detailsContent}>
            <div className="row">
              <div className="col">
                <p className="title">{t("date")}</p>
                <p className="value">{tradeInDetails?.tradeInDate || "-"}</p>
              </div>
              <div className="col">
                <p className="title">Status</p>
                <p className="status value">
                  {tradeInDetails?.deviceProcessingState?.name}
                </p>
              </div>
              <div className="col">
                <p className="title">Trade-In Value</p>
                <p className="value">
                  {tradeInDetails?.pcsNegotiatedValue || "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">Post - Inspection Value</p>
                <p className="value">{tradeInDetails?.pcsDeviceValue || "-"}</p>
              </div>
            </div>
            <div className={classes.divider}></div>
            <div className={classes.customerInfoHeader}>
              <h2 className={classes.sectionTitle}>Customer Information</h2>
              {!isMobile && (
                <Button
                  onClick={() =>
                    history.push(
                      `/TradeInDetails/${tradeinid}/edit-customer-info`
                    )
                  }
                  className={classes.editInfo}
                  size="small"
                  variant="contained"
                  startIcon={<Edit />}
                >
                  Edit Info
                </Button>
              )}
            </div>
            <div className="row">
              <div className="col">
                <p className="title">Full Name</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.firstName}{" "}
                  {tradeInDetails?.ctiQuoteParent?.lastName}
                </p>
              </div>
              <div className="col">
                <p className="title">Shipping Address</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.address1}
                  <br />
                  {tradeInDetails?.ctiQuoteParent?.address2}
                  <br />
                  {tradeInDetails?.ctiQuoteParent?.city},{" "}
                  {tradeInDetails?.ctiQuoteParent?.state}{" "}
                  {tradeInDetails?.ctiQuoteParent?.zip}
                </p>
              </div>
              <div className="col">
                <p className="title">Contact Number</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.phone || "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">Email Address</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.email || "-"}
                </p>
              </div>
              <div>
                {isMobile && (
                  <Button
                    onClick={() =>
                      history.push(
                        `/TradeInDetails/${tradeinid}/edit-customer-info`
                      )
                    }
                    className={classes.editInfo}
                    size="small"
                    variant="contained"
                    startIcon={<Edit />}
                  >
                    Edit Info
                  </Button>
                )}
              </div>
            </div>
            <div className={classes.divider}></div>
            <h2 className={classes.sectionTitle}>Device Details</h2>
            <div className="row">
              <div className="col">
                <p className="title">Manufacturer</p>
                <p className="value">
                  {tradeInDetails?.item?.manufacturer || "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">Model</p>
                <p className="value">{tradeInDetails?.item?.model || "-"}</p>
              </div>
              <div className="col">
                <p className="title">Storage</p>
                <p className="value">
                  {tradeInDetails?.item?.storageCapacity || "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">IMEI</p>
                <p className="value">
                  {tradeInDetails?.originalDeviceId || "-"}
                </p>
              </div>
            </div>
            <div>
              <button
                className={classes.viewImagesBtn}
                onClick={() => toggleViewDeviceImage(true)}
              >
                <ThumbIcon />
                <span>View Device Images</span>
              </button>
            </div>
            <div className={classes.divider}></div>
            <h2 className={classes.sectionTitle}>Inbound Shipping Details</h2>
            <div className="row">
              <div className="col">
                <p className="title">Tracking No.</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.InboundTrackingNumber
                    ?.trackingNumber ||
                    tradeInDetails?.ctiQuoteParent?.trackingNumber ||
                    "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">Tracking Status</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.InboundTrackingNumber
                    ?.carrierShippingStatus || "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">Courier</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.InboundTrackingNumber
                    ?.carrier || "-"}
                </p>
              </div>
            </div>
            <div className={classes.divider}></div>
            <h2 className={classes.sectionTitle}>
              Trade-In Kit Shipping Details
            </h2>
            <div className="row">
              <div className="col">
                <p className="title">Tracking No.</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.RkTrackingNumber
                    ?.trackingNumber ||
                    tradeInDetails?.ctiQuoteParent?.rkTrackingNumber ||
                    "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">Tracking Status</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.RkTrackingNumber
                    ?.carrierShippingStatus || "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">Courier</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.RkTrackingNumber?.carrier ||
                    "-"}
                </p>
              </div>
            </div>
            <div className={classes.divider}></div>
            <h2 className={classes.sectionTitle}>
              Return Device Shipping Details
            </h2>
            <div className="row">
              <div className="col">
                <p className="title">Tracking No.</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.RtcTrackingNumber
                    ?.trackingNumber ||
                    tradeInDetails?.ctiQuoteParent?.rtcTrackingNumber ||
                    "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">Tracking Status</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.RtcTrackingNumber
                    ?.carrierShippingStatus || "-"}
                </p>
              </div>
              <div className="col">
                <p className="title">Courier</p>
                <p className="value">
                  {tradeInDetails?.ctiQuoteParent?.RtcTrackingNumber?.carrier ||
                    "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
        <TradeInTimeLine
          tradeInEvents={tradeInDetails?.events}
          tradeInId={tradeInDetails?.headerExternalId}
        />
      </div>
    </>
  );
};

export default TradeInDetails;